import { reactive } from "vue";

type TabName = "images" | "catalogue";

interface PartHierarchy {
  id: number;
  parentId?: number | null;
}

interface CatalogueSessionState {
  lastCatalogueId: number | null;
  lastPartId: number | null;
  lastTab: TabName;
  expandedPartIds: Set<number>;
  /** ID du désordre vers lequel scroller (réinitialisé après scroll) */
  scrollToDisorderId: number | null;
  setCatalogue: (id: number | null) => void;
  setPart: (id: number | null) => void;
  setTab: (tab: TabName) => void;
  togglePartExpanded: (partId: number) => void;
  setPartExpanded: (partId: number, expanded: boolean) => void;
  isPartExpanded: (partId: number) => boolean;
  /** Navigue vers un désordre spécifique dans le catalogue */
  navigateToDisorder: (
    catalogueId: number,
    partId: number,
    disorderId: number,
    partHierarchy?: PartHierarchy[]
  ) => void;
  clearScrollTarget: () => void;
  reset: () => void;
}

export default reactive<CatalogueSessionState>({
  lastCatalogueId: null,
  lastPartId: null,
  lastTab: "images",
  expandedPartIds: new Set(),
  scrollToDisorderId: null,
  setCatalogue(id: number | null) {
    this.lastCatalogueId = id;
    // Reset part and expanded when catalogue changes
    if (this.lastPartId !== null) {
      this.lastPartId = null;
    }
    this.expandedPartIds = new Set();
  },
  setPart(id: number | null) {
    this.lastPartId = id;
  },
  setTab(tab: TabName) {
    this.lastTab = tab;
  },
  togglePartExpanded(partId: number) {
    if (this.expandedPartIds.has(partId)) {
      this.expandedPartIds.delete(partId);
    } else {
      this.expandedPartIds.add(partId);
    }
  },
  setPartExpanded(partId: number, expanded: boolean) {
    if (expanded) {
      this.expandedPartIds.add(partId);
    } else {
      this.expandedPartIds.delete(partId);
    }
  },
  isPartExpanded(partId: number) {
    return this.expandedPartIds.has(partId);
  },
  navigateToDisorder(
    catalogueId: number,
    partId: number,
    disorderId: number,
    partHierarchy?: PartHierarchy[]
  ) {
    // Set catalogue (without resetting if same)
    if (this.lastCatalogueId !== catalogueId) {
      this.lastCatalogueId = catalogueId;
      this.expandedPartIds = new Set();
    }

    // Set part
    this.lastPartId = partId;

    // Expand all parent parts in hierarchy
    if (partHierarchy) {
      for (const part of partHierarchy) {
        if (part.id !== partId) {
          this.expandedPartIds.add(part.id);
        }
      }
    }

    // Set scroll target
    this.scrollToDisorderId = disorderId;

    // Switch to catalogue tab
    this.lastTab = "catalogue";
  },
  clearScrollTarget() {
    this.scrollToDisorderId = null;
  },
  reset() {
    this.lastCatalogueId = null;
    this.lastPartId = null;
    this.lastTab = "images";
    this.expandedPartIds = new Set();
    this.scrollToDisorderId = null;
  },
});
